import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth } from './AuthContext.jsx'
import {
  GUEST_KITCHEN_CHANGED_EVENT,
  GUEST_KITCHEN_KEY,
  GUEST_SAVE_FAILED_EVENT,
  clearGuestKitchen,
  discardGuestKitchen,
  readGuestKitchen,
  shouldAutoShowKeep,
  startGuestKitchen,
  syncGuestKitchen,
  updateGuestKitchen,
} from '../utils/guestKitchen.js'
import { resolveLoggedOutCohort } from '../utils/homeCohort.js'
import { authenticatedFetch } from '../utils/auth.js'
import { SAVED_LISTS_INVALIDATE_EVENT } from '../savedListsCache.js'

const FALLBACK_GUEST_CONTEXT = {
  authLoading: false,
  cohort: 'newbie',
  kitchen: null,
  start: async () => null,
  startStatus: 'idle',
  saveStatus: 'idle',
  update: () => null,
  retrySave: async () => {},
  finishGuest: () => {},
  discardGuest: () => {},
  remoteSummary: null,
  refreshSummary: () => {},
  shouldAutoShowKeep: false,
}

const GuestKitchenContext = createContext(FALLBACK_GUEST_CONTEXT)

export function GuestKitchenProvider({ children }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [kitchen, setKitchen] = useState(() => readGuestKitchen())
  const [cohort, setCohort] = useState(() => resolveLoggedOutCohort(readGuestKitchen()))
  const [startStatus, setStartStatus] = useState('idle')
  const [saveStatus, setSaveStatus] = useState(
    typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'idle',
  )
  const [remoteSummary, setRemoteSummary] = useState(null)

  useEffect(() => {
    setCohort(isAuthenticated ? 'authenticated' : resolveLoggedOutCohort(kitchen))
  }, [isAuthenticated, kitchen])

  useEffect(() => {
    const onKitchenChanged = (event) => setKitchen(event.detail?.kitchen ?? readGuestKitchen())
    const onStorage = (event) => {
      if (event.key === GUEST_KITCHEN_KEY) setKitchen(readGuestKitchen())
    }
    const onSaveFailed = () => setSaveStatus('error')
    window.addEventListener(GUEST_KITCHEN_CHANGED_EVENT, onKitchenChanged)
    window.addEventListener(GUEST_SAVE_FAILED_EVENT, onSaveFailed)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(GUEST_KITCHEN_CHANGED_EVENT, onKitchenChanged)
      window.removeEventListener(GUEST_SAVE_FAILED_EVENT, onSaveFailed)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const refreshRemoteSummary = useCallback(() => {
    if (!readGuestKitchen() || isAuthenticated) return
    authenticatedFetch('/guest/summary')
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        if (data) setRemoteSummary(data)
      })
      .catch(() => {})
  }, [isAuthenticated])

  useEffect(() => {
    if (!kitchen || isAuthenticated) {
      setRemoteSummary(null)
      return undefined
    }
    refreshRemoteSummary()
    window.addEventListener('recipeImported', refreshRemoteSummary)
    window.addEventListener(SAVED_LISTS_INVALIDATE_EVENT, refreshRemoteSummary)
    window.addEventListener('pantry-inventory-changed', refreshRemoteSummary)
    return () => {
      window.removeEventListener('recipeImported', refreshRemoteSummary)
      window.removeEventListener(SAVED_LISTS_INVALIDATE_EVENT, refreshRemoteSummary)
      window.removeEventListener('pantry-inventory-changed', refreshRemoteSummary)
    }
  }, [isAuthenticated, kitchen, refreshRemoteSummary])

  useEffect(() => {
    const onOffline = () => setSaveStatus('offline')
    const onOnline = () => {
      setSaveStatus('idle')
      if (readGuestKitchen()) void syncGuestKitchen().catch(() => setSaveStatus('error'))
    }
    window.addEventListener('offline', onOffline)
    window.addEventListener('online', onOnline)
    return () => {
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('online', onOnline)
    }
  }, [])

  useEffect(() => {
    if (!kitchen || isAuthenticated) return undefined
    const expiresAt = Date.parse(kitchen.expiresAt || kitchen.createdAt) + (kitchen.expiresAt ? 0 : 24 * 60 * 60 * 1000)
    const delay = Math.max(0, expiresAt - Date.now())
    const timeout = window.setTimeout(() => {
      clearGuestKitchen({ expired: true })
      setKitchen(null)
    }, Math.min(delay, 2_147_483_647))
    return () => window.clearTimeout(timeout)
  }, [kitchen, isAuthenticated])

  const start = useCallback(async () => {
    setStartStatus('busy')
    try {
      const next = await startGuestKitchen()
      setKitchen(next)
      setStartStatus('idle')
      setSaveStatus('idle')
      return next
    } catch (error) {
      setStartStatus('error')
      throw error
    }
  }, [])

  const update = useCallback((updater, { sync = true } = {}) => {
    if (saveStatus === 'offline') throw new Error('Reconnect to keep editing')
    const next = updateGuestKitchen(updater)
    setKitchen(next)
    if (sync && next) {
      setSaveStatus('saving')
      void syncGuestKitchen(next)
        .then(() => setSaveStatus('idle'))
        .catch(() => setSaveStatus('error'))
    }
    return next
  }, [saveStatus])

  const retrySave = useCallback(async () => {
    setSaveStatus('saving')
    try {
      const next = await syncGuestKitchen()
      setKitchen(next)
      setSaveStatus('idle')
    } catch {
      setSaveStatus('error')
    }
  }, [])

  const finishGuest = useCallback(() => {
    clearGuestKitchen()
    setKitchen(null)
    setSaveStatus('idle')
  }, [])

  const discardGuest = useCallback(() => {
    void discardGuestKitchen().catch(() => {
      // Local access is intentionally cleared even if best-effort server deletion fails.
    })
    setKitchen(null)
    setSaveStatus('idle')
  }, [])

  const value = useMemo(() => ({
    authLoading,
    cohort,
    kitchen,
    start,
    startStatus,
    saveStatus,
    update,
    retrySave,
    finishGuest,
    discardGuest,
    remoteSummary,
    refreshSummary: refreshRemoteSummary,
    shouldAutoShowKeep: (
      kitchen?.onboardingComplete === true
      && (
        shouldAutoShowKeep(kitchen)
        || Boolean(
          kitchen
          && !kitchen.keepShown
          && !kitchen.keepDismissed
          && remoteSummary?.pantryCount >= 10
          && remoteSummary?.recipeCount >= 1
        )
      )
    ),
  }), [
    authLoading,
    cohort,
    kitchen,
    retrySave,
    saveStatus,
    start,
    startStatus,
    update,
    finishGuest,
    discardGuest,
    remoteSummary,
    refreshRemoteSummary,
  ])

  return <GuestKitchenContext.Provider value={value}>{children}</GuestKitchenContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGuestKitchen() {
  return useContext(GuestKitchenContext)
}
